import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '../lib/supabase'
import { useAppStore } from '../stores/app'
import {
  getAgentConfigs,
  updateAgentConfig,
  resetAgentConfig,
  resetAllAgentConfigs,
  getPipelineConfigs,
  updatePipelineConfig,
  resetAllPipelineConfigs,
  getAvailableModels,
  getConfigDefaults,
  getPromptHistory,
  restorePromptVersion,
  getTaskReactFlow,
} from '../lib/api'
import type {
  AgentConfigListItem,
  AgentConfigUpdate,
  PipelineConfig,
  ModelInfo,
  PromptHistoryItem,
  ReactFlowTraceExport,
} from '../lib/api'
import { getErrorMessage } from '../lib/errors'

export function useAgentConfigs() {
  return useQuery<AgentConfigListItem[]>({
    queryKey: ['ai-config', 'agents'],
    queryFn: getAgentConfigs,
  })
}

export function useUpdateAgentConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ agentKey, data }: { agentKey: string; data: AgentConfigUpdate }) =>
      updateAgentConfig(agentKey, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config', 'agents'] })
      toast.success('Configuracion del agente guardada')
    },
    onError: (err) => {
      toast.error('Error al guardar configuracion', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useResetAgentConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (agentKey: string) => resetAgentConfig(agentKey),
    onSuccess: (data: AgentConfigListItem) => {
      queryClient.setQueryData<AgentConfigListItem[]>(['ai-config', 'agents'], (old) =>
        old ? old.map((a) => (a.agent_id === data.agent_id ? data : a)) : [data]
      )
      queryClient.invalidateQueries({ queryKey: ['ai-config', 'agents'] })
      toast.success('Configuracion del agente restaurada a defaults')
    },
    onError: (err) => {
      toast.error('Error al restaurar configuracion', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useResetAllAgentConfigs() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resetAllAgentConfigs,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config', 'agents'] })
      queryClient.invalidateQueries({ queryKey: ['ai-config', 'pipelines'] })
      toast.success('Toda la configuracion restaurada a defaults')
    },
    onError: (err) => {
      toast.error('Error al restaurar configuracion', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function usePipelineConfigs() {
  return useQuery<PipelineConfig[]>({
    queryKey: ['ai-config', 'pipelines'],
    queryFn: getPipelineConfigs,
  })
}

export function useUpdatePipelineConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ pipelineKey, data }: { pipelineKey: string; data: { config: Record<string, unknown> } }) =>
      updatePipelineConfig(pipelineKey, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config', 'pipelines'] })
      toast.success('Configuracion del pipeline guardada')
    },
    onError: (err) => {
      toast.error('Error al guardar configuracion del pipeline', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useResetAllPipelineConfigs() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resetAllPipelineConfigs,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config', 'pipelines'] })
      toast.success('Pipelines restaurados a defaults')
    },
    onError: (err) => {
      toast.error('Error al restaurar pipelines', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useAvailableModels() {
  return useQuery<ModelInfo[]>({
    queryKey: ['ai-config', 'models'],
    queryFn: getAvailableModels,
    staleTime: 1000 * 60 * 30,
  })
}

export function useConfigDefaults() {
  return useQuery({
    queryKey: ['ai-config', 'defaults'],
    queryFn: getConfigDefaults,
    staleTime: 1000 * 60 * 60,
  })
}

export function usePromptHistory(agentKey: string) {
  return useQuery<PromptHistoryItem[]>({
    queryKey: ['ai-config', 'agents', agentKey, 'prompt-history'],
    queryFn: () => getPromptHistory(agentKey),
    enabled: !!agentKey,
  })
}

export function useRestorePromptVersion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ agentKey, promptId }: { agentKey: string; promptId: string }) =>
      restorePromptVersion(agentKey, promptId),
    onSuccess: (data: AgentConfigListItem) => {
      queryClient.setQueryData<AgentConfigListItem[]>(['ai-config', 'agents'], (old) =>
        old ? old.map((a) => (a.agent_id === data.agent_id ? data : a)) : [data]
      )
      queryClient.invalidateQueries({ queryKey: ['ai-config', 'agents'] })
      queryClient.invalidateQueries({ queryKey: ['ai-config', 'agents', data.agent_id, 'prompt-history'] })
      toast.success('Version del prompt restaurada')
    },
    onError: (err) => {
      toast.error('Error al restaurar version del prompt', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useTaskReactFlow(taskId: string | null) {
  return useQuery<ReactFlowTraceExport>({
    queryKey: ['ai-config', 'trace', taskId],
    queryFn: () => getTaskReactFlow(taskId!),
    enabled: !!taskId,
    staleTime: 1000 * 60 * 5,
  })
}

export function useLastCompletedTaskId(): string | null {
  const organizationId = useAppStore((state) => state.organizationId)
  const { data } = useQuery<string | null>({
    queryKey: ['content_tasks', organizationId, 'last_completed'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_tasks')
        .select('id')
        .eq('organization_id', organizationId!)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return data?.id ?? null
    },
    enabled: !!organizationId,
    staleTime: 1000 * 30,
  })
  return data ?? null
}
